import ApartmentIcon from '@mui/icons-material/Apartment';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import React from 'react';
import AdminUser from '../../models/admin/AdminUser';
import { ChatMessage, ChatMessageContext } from '../../services/admin/FidesChatAdminService';
import { getChatTimestampMs } from '../../utils/chatTimeline';

export interface ChatContext {
  screen: string;
  platform?: string | null;
  fundOpportunityExternalId?: string | null;
  fundOpportunityName?: string | null;
  investorExternalId?: string | null;
  fundUpdateId?: string | null;
}

export function getPanelInitials(name: string): string {
  const parts = name.split(' ').filter(Boolean).slice(0, 2);
  if (parts.length === 0) {
    return 'A';
  }

  return parts.map((part) => part[0]?.toUpperCase() ?? '').join('');
}

export function adminSendersMatch(left: ChatMessage, right: ChatMessage): boolean {
  const leftId = left.senderId?.trim();
  const rightId = right.senderId?.trim();
  if (leftId && rightId) return leftId === rightId;
  return left.senderName.trim() === right.senderName.trim();
}

/** Show admin sender label only on the first admin message or after a different admin. */
export function shouldShowAdminSenderName(messages: ChatMessage[], index: number): boolean {
  const current = messages[index];
  if (current.senderRole !== 'admin') return false;

  for (let i = index - 1; i >= 0; i -= 1) {
    const previous = messages[i];
    if (previous.senderRole === 'admin') {
      return !adminSendersMatch(previous, current);
    }
  }

  return true;
}

export function formatMessageTime(value: unknown): string {
  const timestamp = getChatTimestampMs(value);

  if (!timestamp) {
    return '';
  }

  return new Intl.DateTimeFormat([], {
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(timestamp));
}

export function buildContextLabel(context: ChatContext | null): string | null {
  if (!context) return null;
  const fundName = context.fundOpportunityName?.trim() || null;
  let label: string;
  switch (context.screen) {
    case 'eoi':
      label = fundName ? `${fundName} – EOI` : 'EOI';
      break;
    case 'fund_update':
      label = fundName ? `${fundName} – Fund Update` : 'Fund Update';
      break;
    case 'portfolio':
      label = 'Portfolio';
      break;
    case 'home':
      label = 'Home';
      break;
    case 'new_funds':
      label = 'New Funds';
      break;
    case 'general':
      label = 'General';
      break;
    default:
      label = context.screen;
  }
  if (context.platform === 'ios') label += ' · iOS';
  else if (context.platform === 'android') label += ' · Android';
  else if (context.platform === 'web') label += ' · Web';
  return label;
}

export function buildContextIcon(screen: string | undefined): React.ReactElement | undefined {
  switch (screen) {
    case 'home':
      return <HomeOutlinedIcon sx={{ fontSize: '1rem !important' }} />;
    case 'portfolio':
      return <ShowChartIcon sx={{ fontSize: '1rem !important' }} />;
    case 'fund_update':
      return <TrendingUpIcon sx={{ fontSize: '1rem !important' }} />;
    case 'eoi':
      return <DescriptionOutlinedIcon sx={{ fontSize: '1rem !important' }} />;
    case 'new_funds':
      return <ApartmentIcon sx={{ fontSize: '1rem !important' }} />;
    default:
      return undefined;
  }
}

/** Returns true if two message contexts represent different screens/funds. */
export function contextsAreDifferent(
  a: ChatMessageContext | null | undefined,
  b: ChatMessageContext | null | undefined,
): boolean {
  if (!a && !b) return false;
  if (!a || !b) return true;
  return (
    a.screen !== b.screen ||
    (a.fundOpportunityExternalId ?? null) !== (b.fundOpportunityExternalId ?? null)
  );
}

/** Web API exposes external IDs as `id` (see WebAdminUser transformForWeb). */
export function panelAdminExternalId(admin: AdminUser): string {
  return admin.externalId || admin.id;
}

export function formatAdminSearchLabel(admin: AdminUser): string {
  const name =
    admin.name?.trim() || [admin.firstName, admin.lastName].filter(Boolean).join(' ').trim();
  if (name && admin.email) {
    return `${name} (${admin.email})`;
  }
  return name || admin.email || panelAdminExternalId(admin);
}
